import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, MenuItem, TextField, Alert, CircularProgress, Typography, Paper,
} from '@mui/material';
import { useGenerarCvu, useEmpresasSinCvu, useCvuConfig } from '../../hooks/useCvus';
import { formatCuit, cuitDigits } from '../../utils/format';
import type { Cvu } from '../../types/cvu';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (cvu: Cvu, warning?: string | null) => void;
}

export default function GenerarCvuDialog({ open, onClose, onSuccess }: Props) {
  const { data: empresas = [], isLoading: loadingEmpresas } = useEmpresasSinCvu();
  const { data: config } = useCvuConfig();
  const generar = useGenerarCvu();

  const [empresaId, setEmpresaId] = useState('');

  useEffect(() => {
    if (open) {
      setEmpresaId('');
      generar.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const empresa = empresas.find((e) => e.id === empresaId);
  const aliasPreview = empresa && config
    ? `${config.alias_prefix}.${cuitDigits(empresa.cuit)}`
    : '';
  const aliasValido = aliasPreview.length > 0 && aliasPreview.length <= 20;

  const handleSubmit = async () => {
    if (!empresaId) return;
    try {
      const { cvu, warning } = await generar.mutateAsync(empresaId);
      onSuccess?.(cvu, warning);
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = generar.error as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generar CVU para una empresa</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {empresas.length === 0 && !loadingEmpresas && (
            <Alert severity="info">
              Todas las empresas activas ya tienen una CVU asignada.
            </Alert>
          )}

          {apiError && (
            <Alert severity="error">
              {apiError.message}
            </Alert>
          )}

          <TextField
            select
            label="Empresa"
            required
            fullWidth
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
            disabled={loadingEmpresas || empresas.length === 0}
            helperText={`${empresas.length} empresa(s) sin CVU`}
          >
            <MenuItem value="">
              <em>Seleccioná una empresa…</em>
            </MenuItem>
            {empresas.map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {e.razon_social} — {formatCuit(e.cuit)}
              </MenuItem>
            ))}
          </TextField>

          {empresa && config && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Vista previa
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>name:</strong>{' '}
                  <code>{cuitDigits(empresa.cuit)}</code>{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (CUIT sin guiones, máx. 12 chars)
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  <strong>alias:</strong> <code>{aliasPreview}</code>{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color={aliasValido ? 'text.secondary' : 'error.main'}
                  >
                    ({aliasPreview.length}/20 chars)
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  <strong>service:</strong> <code>{config.service_code || '—'}</code>
                </Typography>
              </Stack>
              {!aliasValido && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  El alias excede 20 caracteres (límite BCRA). Cambiá el prefijo en{' '}
                  <code>api/.env</code> (<code>PRONTO_PAGO_ALIAS_PREFIX</code>).
                </Alert>
              )}
            </Paper>
          )}

          {config?.mode === 'mock' && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              Modo MOCK: el CVU se genera sintético, no se llama a Pronto Pago.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={generar.isPending}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!empresaId || !aliasValido || generar.isPending}
          startIcon={generar.isPending ? <CircularProgress size={18} /> : null}
        >
          {generar.isPending ? 'Generando…' : 'Generar CVU'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
